import { PlayingCardDTO } from "./DTO";

export class RequestBody {}

export class UpdatePlayingCardsRequest implements RequestBody {
  roomUrlId: string;
  cardDeckType: CardDeckType = CardDeckType.Scrum;
  playingCards: PlayingCardDTO[] = [];

  constructor(roomUrlId: string) {
    this.roomUrlId = roomUrlId;
  }
}

export class CreateRoomRequest  implements RequestBody {
  playerName: string;
  cardDeckType?: CardDeckType;
  playingCards?: PlayingCardDTO[] = [];

 constructor(playerName: string) {
    this.playerName = playerName;
  }
}

export enum CardDeckType {
  Unknown = "",
  Scrum = 0,
  Fibonacci = 1,
  PowersOfTwo = 2,
  TShirt = 3,
  Custom = 4,
}

export interface RoomResponse {
  id: string,
  urlId: string,
  state: string
  players: [{
    id: string,
    userId: string,
    name: string,
    isModerator: boolean,
    isObserver: boolean,
    storyPoint?: string,
    lastSeenAt?: Date,
    connections: [{
      connectionId: number,
      createdAt: Date,
    }]
  }]
  cardDeckType: CardDeckType,
  playingCards: [{
    order: number,
    value: string,
    }],
}

export interface GameInsightsResponse {
    averagePoints?: number,
    averageCard: string,
    alignmentScoreText: string,
}

export interface RecentRoomsResponse {
  recentRoomVisits: [{
    roomUrlId: string,
    playerNames: string,
    daysSinceLastVisit: number,
    player: {
      id: string,
      userId: string,
      name: string,
      isModerator: true
    },
    trend?: PeriodComparisonResponse
  }]
}

export class SignUpRequest  implements RequestBody {
  email: string;
  
 constructor(email: string) {
    this.email = email;
  }
}

export class EnrollModeratorPowersRequest  implements RequestBody {
  roomUrlId: string;
  playerId: string
  
 constructor(roomUrlId: string, playerId: string) {
  this.roomUrlId = roomUrlId;
  this.playerId = playerId;
  }
}

export class UpdatePreferencesRequest implements RequestBody {
  defaultCardDeckType?: CardDeckType;
  defaultPlayingCards?: PlayingCardDTO[];
}

// TypeScript interface for AlignmentTrendPoint
export interface AlignmentTrendPoint {
  x: string; // ISO string for the date
  y: number; // Average alignment score for the day
}

// TypeScript interface for GameInsightAlignmentTrendResponse
export interface GameInsightAlignmentTrendResponse {
  aglinmentPoints: AlignmentTrendPoint[]; // Array of alignment trend points
}

// TypeScript interface for PeriodComparisonResponse
export interface PeriodComparisonResponse {
  averageScoreCurrentPeriod: number;
  averageScorePreviousPeriod: number;
  averageScorePercentageChange: number;
  averageScoreTrendDirection: string; // "improving", "declining", "stable"
  estimationRoundsCurrentPeriod: number;
  estimationRoundsPreviousPeriod: number;
}

// --- Retro Room types ---

export interface RetroPlayer {
  id: string;
  userId: string;
  name: string;
  isModerator: boolean;
  lastSeenAt?: string;
}

export interface RetroRoomResponse {
  urlId: string;
  players: RetroPlayer[];
  state: string;
  template?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RecentRetroRoomItem {
  urlId: string;
  playerNames: string;
  daysSinceLastVisit: number;
  state: string;
}

export interface RecentRetroRoomsResponse {
  recentRetroRooms: RecentRetroRoomItem[];
}

/** Shared fields present on every canvas item (sticky note, text label, …). */
interface CanvasItemBase {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  content: string;
  /**
   * Index (0–5) into the item's color palette.
   * Resolved to a hex at render time based on the active theme mode, so the color
   * adapts automatically when the user switches between light and dark OS themes.
   */
  colorSlot: number;
  /**
   * Stacking order shared by stickies and text labels. Higher draws above lower.
   * Optional for backward compatibility with boards saved before z-order existed.
   */
  zIndex?: number;
  authorId: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
}

export interface StickyState extends CanvasItemBase {
  shape: 'square' | 'rectangle';
}

export interface StickyDiff {
  id: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  content?: string;
  colorSlot?: number;
  zIndex?: number;
  shape?: 'square' | 'rectangle';
  authorId?: string;
  authorName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TextLabelState extends CanvasItemBase {
  /** Font size in px. Controls the rendered size; the element is width: fit-content. */
  fontSize: number;
}

export interface CanvasSnapshot {
  stickies: StickyState[];
  textLabels: TextLabelState[];
}

/** Discriminator for live-drag payloads. Keep in sync with C# CanvasItemKind. */
export type CanvasItemKind = 'sticky' | 'label';

/**
 * Ephemeral live-drag payload. Relay-only — never persisted.
 * One SignalR message carries every item in the current drag.
 */
export interface CanvasItemMove {
  id: string;
  kind: CanvasItemKind;
  x: number;
  y: number;
}

/** Batched deletion payload (cut, bulk delete, undo of a paste). */
export interface CanvasItemsDeleted {
  stickyIds: string[];
  labelIds: string[];
}

/**
 * Compact multi-item move payload — one shared (dx, dy) applied to every listed id
 * instead of a full object per item. Used for the live relay (`itemsMovedDelta`).
 * Keep in sync with C# CanvasItemsMovedDeltaDto.
 */
export interface CanvasItemsMovedDelta {
  stickyIds: string[];
  labelIds: string[];
  dx: number;
  dy: number;
}

/**
 * Broadcast once a compact multi-item move is persisted. Carries no position — the live
 * relay already delivered every increment; ids + updatedAt exist only to keep undo/redo
 * diffing in sync. Keep in sync with C# CanvasItemsMoveCommittedDto.
 */
export interface CanvasItemsMoveCommitted {
  stickyIds: string[];
  labelIds: string[];
  updatedAt: string;
}

/**
 * Compact bring-to-front payload. Assigns zIndex = baseZ, baseZ+1, … to stickyIds in
 * order, then continues the sequence into labelIds. Keep in sync with C#
 * CanvasItemsBroughtToFrontDto.
 */
export interface CanvasItemsBroughtToFront {
  stickyIds: string[];
  labelIds: string[];
  baseZ: number;
  updatedAt: string;
}

/** One occupied canvas item. Ephemeral — never persisted. Keep in sync with C# ItemLockDto. */
export interface ItemLock {
  kind: CanvasItemKind;
  itemId: string;
  playerId: string;
  playerName: string;
}

/** Identifies an item whose occupancy lock was released. Keep in sync with C# ItemLockRefDto. */
export interface ItemLockRef {
  kind: CanvasItemKind;
  itemId: string;
}

/** Result of TryAcquireItemLocks. granted is all-or-nothing. */
export interface TryAcquireItemLocksResult {
  granted: boolean;
  locks: ItemLock[];
  expiredReleased?: ItemLockRef[];
}

/** Occupancy broadcast. Applied as "release these, then acquire these". */
export interface ItemLocksChanged {
  acquired: ItemLock[];
  released: ItemLockRef[];
}

export class SetRetroTemplateRequest implements RequestBody {
  playerId: string;
  /** Template id from the server allowlist, or null to clear the template. */
  template: string | null;

  constructor(playerId: string, template: string | null) {
    this.playerId = playerId;
    this.template = template;
  }
}

// TypeScript interfaces for Pricing API
export interface PricingResponse {
  product: ProductInfo;
  prices: PriceInfo;
  savings?: number;
  currency: string;
}

export interface ProductInfo {
  id: string;
  name: string;
  description: string;
  features: string[];
}

export interface PriceInfo {
  monthly: PriceDetails;
  annual?: PriceDetails;
}

export interface PriceDetails {
  id: string;
  amount: number;
  currency: string;
  interval: string;
  intervalCount: number;
}
